import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Crop } from '../../types/crops';
import { deleteCrop, fetchCrops } from '../../features/crops/cropSlice';
import EditCropForm from './EditCropForm';
import Card from '../ui/Card';
import Button from '../ui/Button';
import ErrorMessage from '../ui/ErrorMessage';
import { formatDate } from '../../utils/formatters';

interface Props {
  crop: Crop;
}

const CropCard: React.FC<Props> = ({ crop }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (window.confirm(t('common.areYouSure'))) {
      setDeleteError(null);
      try {
        const result = await dispatch(deleteCrop(crop.id));
        
        if (deleteCrop.rejected.match(result)) {
          const error = result.payload as any;
          
          if (error?.type === 'CROP_HAS_SALES') {
            setDeleteError(
              `${t('crops.errors.cannotDeleteWithSales')} ${error.salesCount} ${
                error.salesCount === 1 ? t('sales.sale') : t('sales.sales')
              }.`
            );
          } else {
            setDeleteError(error?.message || t('crops.errors.deleteFailed'));
          }
        }
      } catch (error) {
        setDeleteError(t('crops.errors.deleteFailed'));
      }
    }
  };

  const getStatusColor = (status: Crop['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'harvested':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isEditing) {
    return (
      <EditCropForm
        crop={crop}
        onCancel={() => setIsEditing(false)}
        onSuccess={() => {
          setIsEditing(false);
          // Refresh crops list after editing
          dispatch(fetchCrops());
        }}
      />
    );
  }

  return (
    <Card>
      <div className="space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{crop.name}</h3>
            {crop.variety && (
              <p className="text-sm text-gray-600">
                {t('crops.variety')}: {crop.variety.name}
              </p>
            )}
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(crop.status)}`}>
            {t(`crops.status.${crop.status}`)}
          </span>
        </div>

        <div className="space-y-2 text-sm text-gray-600">
          {crop.planting_date && (
            <p>{t('crops.plantingDate')}: {formatDate(crop.planting_date)}</p>
          )}
          {crop.expected_harvest_date && (
            <p>{t('crops.harvestDate')}: {formatDate(crop.expected_harvest_date)}</p>
          )}
          {crop.notes && <p className="italic">{crop.notes}</p>}
        </div>

        {deleteError && (
          <ErrorMessage message={deleteError} onClose={() => setDeleteError(null)} />
        )}

        <div className="flex justify-end space-x-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setIsEditing(true)}
          >
            {t('common.edit')}
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
          >
            {t('common.delete')}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default CropCard;